import { SearchIcon } from '@chakra-ui/icons'
import { Avatar, Badge, Box, FormControl, InputGroup, FormLabel, HStack, Text } from '@chakra-ui/react'
import { components as SelectComponents } from 'react-select'
import { Select } from 'chakra-react-select'
import last from 'lodash/last'
import { Controller, useFormContext } from 'react-hook-form'
import { IOptionWithIcon } from 'types/common'
import { AUTH_MOBILE_INPUT_BORDER, DARK_PAGE_BG, DARK_TEXT_MUTED } from 'constants/dark-tokens'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'

export interface IChakraInputDropdownProps {
  name: string
  label: string
  optionsData: IOptionWithIcon<string>[]
  placeholder?: string
  selectedData?: IOptionWithIcon<string>[]
  chooseOptionsHandler: (value: string) => void
  hasIcon?: boolean
  hasAvatar?: boolean
  /** Opt-in dark-surface styling (default light). */
  dark?: boolean
  /** Menu open direction (react-select). Default 'bottom'. */
  menuPlacement?: 'top' | 'bottom' | 'auto'
  /** Portal the menu (e.g. document.body) so it escapes overflow clipping. */
  menuPortalTarget?: HTMLElement | null
}

const CustomInputDropdown = (props: IChakraInputDropdownProps) => {
  const { name, label, optionsData, placeholder, chooseOptionsHandler, hasIcon, hasAvatar, dark, menuPlacement, menuPortalTarget } = props

  const { control } = useFormContext()
  const renderOptionContent = (optionProps: any) => (
    <HStack spacing={2} width="full">
      {hasIcon && optionProps?.data?.icon && <IconBuilder icon={optionProps.data.icon} isActive size={32} />}
      {hasAvatar && (
        <Avatar size="sm" name={optionProps?.data?.label} src={optionProps?.data?.avatarUrl ?? ''} />
      )}
      <Text>{optionProps?.data?.label ?? ''}</Text>
      {optionProps?.data?.tagLabel && (
        <Badge size="sm" colorScheme="blue" variant="subtle" ml={1}>
          {optionProps.data.tagLabel}
        </Badge>
      )}
    </HStack>
  )

  const components: Record<string, unknown> = {
    IndicatorSeparator: null,
    DropdownIndicator: () => <SearchIcon boxSize={5} color={dark ? 'whiteAlpha.600' : 'gray.600'} mr={2} />,
    Option: (optionProps: any) => {
      // react-select's base Option paints its own emotion styles (default focused
      // bg #DEEBFF) that chakraStyles can't override, so on the dark surface we
      // render the option ourselves via innerRef/innerProps (keeps click, focus,
      // aria, scroll-into-view) with the standard faint wash instead.
      if (dark) {
        const { innerRef, innerProps, isFocused, isSelected } = optionProps
        const bg = isSelected ? 'rgba(255, 255, 255, 0.12)' : isFocused ? 'rgba(255, 255, 255, 0.08)' : 'transparent'
        return (
          <Box
            ref={innerRef}
            {...innerProps}
            px={3}
            py="10px"
            cursor="pointer"
            color="white"
            background={bg}
          >
            {renderOptionContent(optionProps)}
          </Box>
        )
      }
      return (
        <SelectComponents.Option {...optionProps}>
          {renderOptionContent(optionProps)}
        </SelectComponents.Option>
      )
    }
  }

  return (
    <FormControl width="full" id={name}>
      <FormLabel
        color={dark ? DARK_TEXT_MUTED : 'gray.700'}
        fontFamily="Poppins"
        fontWeight="500"
        lineHeight={6}
        marginBottom={2}
      >
        {label}
      </FormLabel>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InputGroup
            borderRadius="6px"
            width="full"
            background={dark ? 'transparent' : 'white'}
            cursor="pointer"
            isolation="unset"
          >
            <Select
              {...field}
              value={null}
              onChange={options => {
                chooseOptionsHandler(last(options)?.value ?? '')
              }}
              isSearchable
              isMulti
              options={optionsData}
              placeholder={placeholder}
              closeMenuOnSelect={true}
              components={components}
              size="md"
              isClearable
              menuShouldBlockScroll={false}
              menuPlacement={menuPlacement}
              menuPortalTarget={menuPortalTarget}
              menuPosition={menuPortalTarget ? 'fixed' : undefined}
              chakraStyles={{
                container: (provided: Record<string, unknown>) => ({
                  ...provided,
                  width: 'full',
                  cursor: 'pointer'
                }),
                control: (provided: Record<string, unknown>) => ({
                  ...provided,
                  ...(dark
                    ? {
                        background: 'transparent',
                        borderColor: AUTH_MOBILE_INPUT_BORDER,
                        color: 'white'
                      }
                    : {})
                }),
                input: (provided: Record<string, unknown>) => ({
                  ...provided,
                  ...(dark ? { color: 'white' } : {})
                }),
                option: (provided: Record<string, unknown>, state: { isFocused?: boolean; isSelected?: boolean }) => ({
                  ...provided,
                  width: 'auto',
                  cursor: 'pointer',
                  ...(dark
                    ? {
                        // Selected stays subtle (chakra-react-select defaults to a
                        // bright accent fill); hover/focus is the standard faint wash.
                        background: state.isSelected
                          ? 'rgba(255, 255, 255, 0.12)'
                          : state.isFocused
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'transparent',
                        color: 'white',
                        ':active': { background: 'rgba(255, 255, 255, 0.12)' }
                      }
                    : {})
                }),
                dropdownIndicator: (provided: Record<string, unknown>) => ({
                  ...provided,
                  // bg: 'transparent',
                  px: 2,
                  cursor: 'pointer'
                }),
                indicatorSeparator: (provided: Record<string, unknown>) => ({
                  ...provided,
                  display: 'none'
                }),
                clearIndicator: (provided: Record<string, unknown>) => ({
                  ...provided,
                  display: 'none'
                }),
                multiValueRemove: (provided: Record<string, unknown>) => ({
                  ...provided,
                  color: dark ? 'white' : 'gray.700'
                }),
                placeholder: (provided: Record<string, unknown>) => ({
                  ...provided,
                  padding: '0',
                  ...(dark ? { color: 'rgba(255, 255, 255, 0.5)' } : {})
                }),
                menu: (provided: Record<string, unknown>) => ({
                  ...provided,
                  zIndex: 9999
                }),
                menuList: (provided: Record<string, unknown>) => ({
                  ...provided,
                  zIndex: 9999,
                  ...(dark ? { background: DARK_PAGE_BG, borderColor: 'rgba(194, 199, 214, 0.12)' } : {})
                }),
                // @ts-ignore
                menuPortal: (provided: Record<string, unknown>) => ({
                  ...provided,
                  zIndex: 9999
                })
              }}
            />
          </InputGroup>
        )}
      />
    </FormControl>
  )
}

export default CustomInputDropdown
